import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';

import type { Item, ProductCategoryFilter } from 'types';
import productCategories, { CategoryDto } from 'components/Collection/pages/categories';
import type { ColorFilter } from 'partials/Visualizer';
import {
  getParameterByName,
  combinePredicatesUsingOr,
  constructQueryStringFromFilters,
  capitalizeFirstLetter,
} from 'utils/utils';

type UseCollectionFiltersProps = {
  activeCategory: CategoryDto;
  products: Item[];
};

const parseFiltersFromUrl = (filters: string): string[] =>
  decodeURIComponent(filters)
    .split(',')
    .map((filter) => filter.replace(/\+/g, ' ').split(' ').map(capitalizeFirstLetter).join(' '));

export const useCollectionFilters = ({ products, activeCategory }: UseCollectionFiltersProps) => {
  const router = useRouter();

  const [categoryFilters, setCategoryFilters] = useState<ProductCategoryFilter[]>([]);
  const [colorFilters, setColorFilters] = useState<ColorFilter[]>([]);
  const [patternFilters, setPatternFilters] = useState<string[]>([]);
  const [procurementFilters, setProcurementFilters] = useState<string[]>([]);
  const [tieTypeFilters, setTieTypeFilters] = useState<string[]>([]);

  const getFiltersMatchingActiveCategory = (categories: CategoryDto[]): ProductCategoryFilter[] => {
    const matchingCategory = categories.find((category) => category.displayName.includes(activeCategory.displayName));
    return matchingCategory ? matchingCategory.categoryFilters : [];
  };

  useEffect(() => {
    if (!router.isReady) return;

    const type = getParameterByName('type');
    const tieType = getParameterByName('tieType');
    const color = getParameterByName('color');
    const pattern = getParameterByName('pattern');
    const procurement = getParameterByName('procurement');

    const allPossibleCategoryFiltersForType = type ? getFiltersMatchingActiveCategory(productCategories) : [];
    const initialCategoryFilters = allPossibleCategoryFiltersForType.filter((filter) =>
      parseFiltersFromUrl(type || '').includes(filter.displayName)
    );
    const initialTieTypeFilters = tieType ? parseFiltersFromUrl(tieType) : [];
    const initialColorFilters = color ? (parseFiltersFromUrl(color) as ColorFilter[]) : [];
    const initialPatternFilters = pattern ? parseFiltersFromUrl(pattern) : [];
    const initialProcurementFilters = procurement ? parseFiltersFromUrl(procurement) : [];

    setCategoryFilters(initialCategoryFilters);
    setColorFilters(initialColorFilters);
    setPatternFilters(initialPatternFilters);
    setProcurementFilters(initialProcurementFilters);
    setTieTypeFilters(initialTieTypeFilters);
  }, [router.isReady, activeCategory.displayName, productCategories]);

  useEffect(() => {
    if (!router.isReady) return;

    const filtersMap = {
      color: colorFilters,
      pattern: patternFilters,
      type: categoryFilters,
      procurement: procurementFilters,
      tieType: tieTypeFilters,
    };

    const newQueryString = constructQueryStringFromFilters(filtersMap);
    const currentQueryString = router.asPath.split('?')[1] || '';

    if (newQueryString !== currentQueryString) {
      let newPath = router.pathname;

      if (router.query.category && typeof router.query.category === 'string') {
        newPath = router.pathname.replace('[category]', router.query.category);

        if (router.query.slug && typeof router.query.slug === 'string') {
          newPath = newPath.replace('[slug]', router.query.slug);
        }
      } else {
        newPath = window.location.pathname;
      }

      if (newQueryString) {
        newPath += `?${newQueryString}`;
      }

      if (newPath !== router.asPath) {
        router.replace(newPath, undefined, { shallow: true, scroll: false });
      }
    }
  }, [colorFilters, patternFilters, categoryFilters, procurementFilters, tieTypeFilters, router]);

  const getFilteredProducts = () => {
    const filtered = products.filter((product) => {
      if (categoryFilters.length > 0) {
        const combinedPredicates = combinePredicatesUsingOr(categoryFilters.map((filter) => filter.value));
        const values = [product.display_name!];

        if (product.category !== 'Tie') values.push(product.category!);
        if (product.category === 'preconfigured' && product.type) values.push(product.type);
        if (!values.some(combinedPredicates)) return false;
      }

      if (patternFilters.length > 0 && !patternFilters.includes(product.pattern!)) return false;
      if (colorFilters.length > 0 && !colorFilters.includes(product.color as ColorFilter)) return false;
      if (tieTypeFilters.length > 0 && !tieTypeFilters.includes(product.attributeSecondary!)) return false;

      if (procurementFilters.length === 1) {
        const lowerCaseProcurementFilters = procurementFilters.map((filter) => filter.toLowerCase());

        if (lowerCaseProcurementFilters.includes('buy')) {
          if (
            !(product.is_retail || product.retail_bundle?.displayable || product.type?.toLowerCase() === 'purchase')
          ) {
            return false;
          }
        }

        if (lowerCaseProcurementFilters.includes('rent')) {
          if (!(product.is_retail === false || product.type?.toLowerCase() === 'rental')) return false;
        }
      }

      return true;
    });

    const sortByDisplayIndex = (prods: Item[]) =>
      [...prods].sort((a, b) => Number(a.display_index ?? 999) - Number(b.display_index ?? 999));

    const sortPocketSquares = (prods: Item[]) =>
      [...prods].sort((a, b) => {
        const haveMatchingCategories = a.category === b.category;

        if (a.category === 'Pocket Square' && !haveMatchingCategories) return -1;
        if (b.category === 'Pocket Square' && !haveMatchingCategories) return 1;

        return 0;
      });

    let sortedProducts = sortByDisplayIndex(filtered);

    if (activeCategory.name.includes('Pocket Square')) {
      sortedProducts = sortPocketSquares(sortedProducts);
    }

    return sortedProducts;
  };

  const filteredProducts = getFilteredProducts();

  const toggleCategoryFilters = (categoryFilter: ProductCategoryFilter) => {
    setCategoryFilters((prev) =>
      prev.some((f) => f.displayName === categoryFilter.displayName)
        ? prev.filter((filter) => filter.displayName !== categoryFilter.displayName)
        : [...prev, categoryFilter]
    );
  };

  const toggleColorFilters = (colorFilter: ColorFilter) => {
    setColorFilters((prev) =>
      prev.includes(colorFilter) ? prev.filter((c) => c !== colorFilter) : [...prev, colorFilter]
    );
  };

  const togglePatternFilters = (patternFilter: string) => {
    setPatternFilters((prev) =>
      prev.includes(patternFilter) ? prev.filter((p) => p !== patternFilter) : [...prev, patternFilter]
    );
  };

  const toggleProcurementFilters = (procurementFilter: string) => {
    setProcurementFilters((prev) =>
      prev.includes(procurementFilter) ? prev.filter((r) => r !== procurementFilter) : [...prev, procurementFilter]
    );
  };

  const toggleTieTypesFilters = (tieTypeFilter: string) => {
    setTieTypeFilters((prev) =>
      prev.includes(tieTypeFilter) ? prev.filter((filter) => filter !== tieTypeFilter) : [...prev, tieTypeFilter]
    );
  };

  return {
    filteredProducts,
    categoryFilters,
    colorFilters,
    patternFilters,
    procurementFilters,
    tieTypeFilters,
    toggleCategoryFilters,
    toggleColorFilters,
    togglePatternFilters,
    toggleProcurementFilters,
    toggleTieTypesFilters,
  };
};
